/* eslint-disable arrow-body-style, consistent-return, no-param-reassign, no-confusing-arrow, react/react-in-jsx-scope, react/jsx-closing-bracket-location */
import * as API from 'es6/core/api';
import BaseStudyApp from 'es6/controllers/apps/base-study-app';
import { checkForcedCheckpoint, checkForcedCompletion } from 'es6/controllers/apps/debug-overrides';
import Config from 'es6/core/config';
import CourseRecommendationScreen from 'es6/components/apps/screens/course-recommendation';
import HelpPanel from 'es6/components/apps/settings/help-panel';
import { GoalSource, ItemSource } from 'es6/models/session-config';
import * as Loaders from 'es6/core/loaders';
import Localization from 'es6/services/localization';
import Logger from 'es6/services/logger';
import MemoryBankNode from 'es6/models/memory-bank-node';
import MemoryBankPlot from 'es6/components/memory-bank/memory-bank-plot';
import MemoryBankSummaryScreen, { MemoryBankSummaryBanner } from 'es6/components/apps/screens/memory-bank-summary';
import Memory from 'es6/models/memory';
import * as Paths from 'es6/core/paths';

// This has to persist between apps for a blue box session. We don't
// want to recommend a user a course repeatedly for starting a new
// app after they have already seen it.
let courseRecommended = false;

// Message to show user if they try to leave during a checkpoint.
const SkipCheckpointPrompt = Localization.t('skip-checkpoint-prompt');

/**
 * Base class for Item Study Applications: iKnow, Rapid Choice, Self Assessment.
 */
export default class BaseItemStudyApp extends BaseStudyApp {
  /**
   * Constructor for the base app.
   */
  constructor(...args) {
    super(...args);

    // Information pertaining to the recommended course (if available).
    this._courseRecommendation = null;

    // Include sentence assets when loading item packages?
    this._includeSentenceAssets = true;

    // These flags are used to determine whether a course was completed in session.
    // The This flag is for when the course was completed in session.
    this._isCourseCompleted = false;

    // Has the user reached a checkpoint.
    this._isCheckpoint = false;

    // Is it time to show a course recommendation.
    this._isCourseRecommendation = false;

    // Contains a copy of MemoryBankPlot used to render the memory bank SVG.
    this._memoryBankPlot = null;
  }

  /**
   * Determines whether or not to set the checkpoint flag.
   *
   * @param {Object} sessionResults     results from the last session
   * @returns {Boolean}
   */
  _checkpointTriggered(sessionResults) {
    return !this._studyAll && this._sessionData.goals[0].sentencesCount > 0 &&
      (checkForcedCheckpoint() || sessionResults.checkpointGoals.some(cp => cp.goalId === this._goalId));
  }

  /**
   * Determines whether or not to set the course completed flag.
   *
   * @param {Object} sessionResults     results from the last session
   * @returns {Boolean}
   */
  _courseCompleted(sessionResults) {
    return !this._studyAll &&
      (checkForcedCompletion() || sessionResults.completedGoals.some(goalId => goalId === this._goalId));
  }

  /**
   * For item study apps, the course recommendation might need to be retrieved.
   *
   * @override
   * @returns {Promise}
   */
  _fetchSessionBasedData() {
    return Promise.all([super._fetchSessionBasedData(), this._setupCourseRecommendation()]);
  }

  /**
   * Add help panel to the settings modal.
   *
   * @override
   * @returns {Array}
   */
  _getSettingsPanels() {
    const panels = super._getSettingsPanels();
    panels.push(HelpPanel);
    return panels;
  }

  /**
   * Add item source to the study parameters.
   *
   * @override
   * @returns {Object}
   */
  _getStudyParameters() {
    const params = super._getStudyParameters();
    params.sessionItemSource = this._sessionConfig.itemSource;
    return params;
  }

  /**
   * Callback for redirecting the user to sentence trainer.
   */
  _handleCheckpointRedirect() {
    // No need to check if the user wants to leave beforehand.
    window.removeEventListener('beforeunload', this._handleBeforeUnload);
    this._quitApplication(Paths.AppPaths.SENTENCE_TRAINER, true);
  }

  /**
   * Callback for skipping checkpoints.
   */
  _handleCheckpointSkip() {
    this._isCheckpoint = false;
    this._showWelcomeScreen();
  }

  /**
   * Callback method for skipping the course recommendation.
   */
  _handleCourseRecommendationSkip() {
    this._isCourseRecommendation = false;
    this._showWelcomeScreen();
  }

  /**
   * Callback method for show course recommendation screen.
   *
   * Focus the course (if it is a new course).
   * After, if the user is in studyAll mode, refresh the study session.
   * Otherwise, redirect the user to the single goal page.
   */
  _handleCourseRecommendationStart() {
    this._isCourseRecommendation = false;

    this._loading(true);
    this._screenManager.show('loading');

    // If the course isn't focused, focus it.
    let promise = Promise.resolve(true);
    if (!this._courseRecommendation.aggregateMemory.focused) {
      promise = API.postFocusGoal(this._courseRecommendation.goal.id);
    }

    promise.then(() => {
      if (this._studyAll) {
        this._fetchData();
      } else {
        window.location.href = Paths.studyGoal(this._courseRecommendation.goal.id);
      }
    })
      .catch(this._errorHandler.bind(this));
  }

  /**
   * @override
   * @param {Object[]} presentationResults    results sent
   * @param {Object}   sessionResults         response from the server
   */
  _handlePresentationResultsUploaded(presentationResults, sessionResults) {
    // Check to see if the goal was completed (this only applies to single course).
    this._isCourseCompleted = this._courseCompleted(sessionResults);

    // Check to see if the checkpoint was returned.
    this._isCheckpoint = this._checkpointTriggered(sessionResults);

    super._handlePresentationResultsUploaded(presentationResults, sessionResults);
  }

  /**
   * Callback for skipping multiple items on the welcome screen(s).
   *
   * @param {Number[]} itemIds
   */
  _handleSkipItems(itemIds) {
    // Don't do anything if in the loading process.
    if (this._isLoading) {
      return;
    }

    this._loading(true);

    const skippedItemCalls = _.map(itemIds, (itemId) => {
      return API.postSkipItem(itemId);
    });

    Promise.all(skippedItemCalls)
      .then(() => {
        // Update item package data to set memory data to skipped.
        itemIds.forEach(itemId => {
          const itemPackage = this._sessionData.itemPackages[itemId];

          if (itemPackage.memory) {
            itemPackage.memory.skipped = true;
          } else {
            itemPackage.memory = new Memory({ skipped: true });
          }
        });

        // Skipping items will alter aggregate data, reload
        // the memories when fetching the study session.
        this._reloadMemories();
        this._fetchData();
      })
      .catch((reason) => {
        Logger.error('Skipping Items', reason, reason.stack);
        this._fetchData();
      });
  }

  /**
   * Method for loading item package assets.
   *
   * @override
   * @param {Boolean} includeSentenceAssets
   * @returns {Loader}
   */
  _loadSessionAssets() {
    const itemPackages = this._sessionData.studySession.map(studyNode => studyNode.content);
    return new Loaders.ItemPackageAssets(itemPackages, this._includeSentenceAssets);
  }

  /**
   * Determines whether the current state of the session data makes
   * the course eligible for a recommendation. If so, retrieve the
   * recommendation data.
   *
   * Because course the recommendation only appears once for each
   * bluebox, use courseRecommend to determine whether the
   * course was already recommended or not.
   *
   * @returns {Promise}
   */
  _setupCourseRecommendation() {
    if (courseRecommended) {
      this._isCourseRecommendation = false;
      return Promise.resolve(false);
    }

    // For studyAll, make sure the item source and goal source are set to
    // All. If all the items in the study session are strong, then the
    // course is eligible for a recommendation.
    //
    // For a single goal, check to see if there are no ready or no new items.
    if (this._studyAll) {
      courseRecommended =
        this._sessionConfig.goalSource === GoalSource.ALL &&
        this._sessionConfig.itemSource === ItemSource.ALL &&
        this._sessionData.studySession.every(s => s.memory && !s.memory.isReady());
    } else {
      courseRecommended = this._sessionData.goals[0].getReadyItemsCount(this._sessionData.aggregateMemories[0]) === 0;
    }

    // Simply resolve if nothing has changed.
    if (!courseRecommended) {
      return Promise.resolve(false);
    }

    // Set up promise as a response.
    const loader = new Loaders.GoalSuggestionLoader(this._cueLanguage);
    return loader.then(response => {
      this._courseRecommendation = response;

      // If the response wasn't truthy, then there is no course to study.
      // Reset the course recommended and isCourseRecommendation flag so
      // the lookup can happen again.
      if (!response) {
        courseRecommended = false;
        this._isCourseRecommendation = false;
      } else {
        this._isCourseRecommendation = true;
      }

      return this._isCourseRecommendation;
    });
  }

  /**
   * Listen for beforeunload event to warn user that they are
   * leaving a checkpoint.
   *
   * @override
   */
  _setupEvents() {
    super._setupEvents();

    // Helper method that needs to be stored as a bound method for binding
    // and unbinding event.
    this._handleBeforeUnload = (e) => {
      // If the state is welcome screen checkpoint, then prompt
      // the user before they can leave the page. Some browsers
      // require returnValue to set on the event itself.
      // https://developer.mozilla.org/en-US/docs/Web/Events/beforeunload#Example
      if (this._isCheckpoint) {
        (e || window.event).returnValue = SkipCheckpointPrompt;
        return SkipCheckpointPrompt;
      }

      // Force the session to be saved locally if there is one running.
      this._forceFinishSession(true);
    };

    window.addEventListener('beforeunload', this._handleBeforeUnload);
  }

  /**
   * Unbind listener for checkpoints, memory bank, etc.
   *
   * @override
   */
  _quitApplication(...args) {
    if (this._isLoading) {
      return;
    }

    // If a plot was generated, delete it.
    if (this._memoryBankPlot) {
      this._memoryBankPlot.remove();
    }

    // Unbind window events.
    window.removeEventListener('beforeunload', this._handleBeforeUnload);

    // Remove master and skip events.
    this._skipItemDialogue.removeEvent('item_skipped');
    this._completeItemDialogue.removeEvent('item_skipped');

    // In Study All, clear memories from the item packages (since they are
    // cached), but should not persist between apps.
    if (this._studyAll && this._sessionData && this._sessionData.itemPackages) {
      const itemPackages = this._sessionData.itemPackages;
      Object.keys(itemPackages).map(id => delete itemPackages[id].memory);
    }

    super._quitApplication(...args);
  }

  /**
   * Render the course recommendation page.
   */
  _showCourseRecommendation() {
    this._screenManager.renderComponent(CourseRecommendationScreen, {
      aggregateMemory: this._courseRecommendation.aggregateMemory,
      goal: this._courseRecommendation.goal,
      onMenu: () => this._quitApplication(),
      onStart: this._handleCourseRecommendationStart.bind(this),
      onSkip: this._handleCourseRecommendationSkip.bind(this),
    });

    // Reset the banner.
    this._renderBanner();
  }

  /**
   * For items, always just retrieve the next session data and show
   * the memory bank.
   */
  _showSummaryScreen() {
    this._screenManager.show('loading');
    this._fetchData();
  }

  /**
   * Renders the memory bank component, which renders the session preview,
   * session summary and memory bank components.
   */
  _showWelcomeScreen() {
    let updateMemoryBankPlot = true;

    // Build the memory bank nodes for the next session. For study all, we only
    // want to add items that are in the session or have had a memory assigned
    // to them. For single study, we can create a node for all items.
    const inSessionNodes = new Set(this._sessionData.studySession.map(studyNode => studyNode.content));
    const memoryBankNodes = _.chain(this._sessionData.itemPackages)
      .filter(ip => this._studyAll ? (inSessionNodes.has(ip) || !!ip.memory) : true)
      .map(ip => {
        const inSession = inSessionNodes.has(ip);
        return MemoryBankNode.createFromItemPackage(ip, ip.memory, inSession);
      })
      .value();

    // Set up memory bank plot if it already exist.
    // Don't use the update method to render the nodes.
    if (!this._memoryBankPlot) {
      this._memoryBankPlot = new MemoryBankPlot(memoryBankNodes);
      updateMemoryBankPlot = false;
    }

    // If there is a course recommendation and a checkpoint, ignore the checkpoint.
    const isCheckpoint = this._isCheckpoint && !this._isCourseRecommendation;

    // Set up the props to send to the screen manager.
    const props = {
      applicationType: this._applicationType,
      courseRecommendation: this._isCourseRecommendation ? this._courseRecommendation : null,
      isCheckpoint,
      isCourseCompleted: this._isCourseCompleted,
      isCourseRecommendation: this._isCourseRecommendation,
      isPremiumUser: Config.user.isPremium,
      isStudyAll: this._studyAll,
      hasFinishedSession: this._finishedSessionsCount > 0,
      memoryBankPlot: this._memoryBankPlot,
      onCheckpointSkip: this._handleCheckpointSkip.bind(this),
      onCheckpointRedirect: this._handleCheckpointRedirect.bind(this),
      onCourseRecommendationShow: this._showCourseRecommendation.bind(this),
      onCourseRecommendationSkip: this._handleCourseRecommendationSkip.bind(this),

      // After the complete item prompt goes away, re-render component.
      onMasterItem: (itemPackage) => {
        this._completeItemPrompt(itemPackage, this._showWelcomeScreen.bind(this));
      },

      onMenu: () => this._quitApplication(),
      onNext: () => {
        this._isCourseCompleted = false;
        this._showWelcomeScreen();
      },

      onGoalSourceChange: this._handleSessionChange.bind(this, 'goalSource'),
      onItemSourceChange: this._handleSessionChange.bind(this, 'itemSource'),
      onSessionSizeChange: this._handleSessionChange.bind(this, 'sessionSize'),

      onSkipItems: this._handleSkipItems.bind(this),
      onStart: (...args) => this._startSession(...args),
      sessionConfig: this._sessionConfig,
      sessionData: this._sessionData,
      sessionSizes: this._getSessionSizes(),
      startLabel: this._getStartButtonLabel(),
      updatedMemories: this._updatedMemories,
    };

    this._screenManager.renderComponent(
      MemoryBankSummaryScreen,
      props,
      () => {
        // Now that the component has rendered, update the memory bank nodes
        if (updateMemoryBankPlot) {
          this._memoryBankPlot.update(memoryBankNodes);
        }
      }
    );

    this._renderBanner({
      showSettingsButton: true,
      children: (
        <MemoryBankSummaryBanner
          isStudyAll={props.isStudyAll}
          sessionData={props.sessionData} />
      ),
    });
  }

  /**
   * @override
   * @returns {Boolean}
   */
  _updateSessionConfig() {
    let hasNewItems = true;
    let hasReviewItems = true;
    let hasCompleted = true;
    let hasUncompleted = true;

    if (this._studyAll) {
      // Not checking all goals for review items, but seeing if are any started
      // courses.
      hasReviewItems = _.some(this._sessionData.aggregateMemories, (memory) => {
        return memory && memory.items && memory.items.studiedItemsCount;
      });

      hasCompleted = _.some(this._sessionData.aggregateMemories, (memory) => {
        return memory && memory.completed;
      });

      hasUncompleted = _.some(this._sessionData.aggregateMemories, (memory) => {
        return !memory || !memory.completed;
      });
    } else {
      hasNewItems = _.some(this._sessionData.itemPackages, (ip) => {
        return !ip.memory;
      });

      hasReviewItems = _.some(this._sessionData.itemPackages, (ip) => {
        return ip.memory && !ip.memory.skipped;
      });
    }

    // Keep all the results from toggling each field.
    const toggleResults = [
      this._sessionConfig.toggleDisabled('itemSource', ItemSource.NEW, !hasNewItems),
      this._sessionConfig.toggleDisabled('itemSource', ItemSource.REVIEW, !hasReviewItems),
      this._sessionConfig.toggleDisabled('goalSource', GoalSource.COMPLETED, !hasCompleted),
      this._sessionConfig.toggleDisabled('goalSource', GoalSource.UNCOMPLETED, !hasUncompleted),
    ];

    // If any of the fields forced a config to change, return false.
    return toggleResults.reduce((a, b) => a && b);
  }

  /**
   * Should return a translated string for the app.
   */
  _getStartButtonLabel() {
    throw new Error('Item Study Apps must define _getStartButtonLabel()');
  }
}
